import type { Emitter } from "@core/events/emitter";
import type { MediaLibraryKind } from "@models/data/mediaLibraryDto";

/** One card of the Asset Library's **Media** tab. */
export interface MediaLibraryItem {
  /** `media/{fileName}` — the identity of a library file. */
  readonly storagePath: string;
  readonly fileName: string;
  readonly kind: MediaLibraryKind;
  /** Upper-cased extension (`"MP4"`), the badge shown when there is no preview. */
  readonly format: string;
  readonly fileSize: number;
  /** Uploader's name tag (`UserInfo.nameTag`), same as an asset's `UserTag`. */
  readonly userTag: string;
  /** Parsed `LastModifiedUtc` (epoch ms; 0 when unparseable → sorts last). */
  readonly lastModifiedMs: number;
  /** The `campfire://media/…` reference to paste into a workflow step's `media.url`. */
  readonly url: string;
  /** The stored `Content-Type`, so a read serves bytes a `<video>` will accept. */
  readonly contentType: string;
}

export interface MediaUploadResult {
  readonly uploaded: number;
  readonly failed: number;
  /** Rejected before upload for an unsupported extension. */
  readonly rejected: ReadonlyArray<string>;
  /** The urls of the files that landed, in upload order. */
  readonly urls: ReadonlyArray<string>;
}

/**
 * One file in an upload batch, so the library can show it as a placeholder tile
 * while it is on its way up instead of blanking the grid behind a spinner.
 *
 * The states are per FILE, which is the finest honest granularity available: the
 * browser S3 client sends a whole `Uint8Array` body through `fetch`, which
 * reports no upload progress, so a percentage here would be invented. "This one
 * is going up, these three are next" is true.
 */
export interface PendingMediaUpload {
  readonly id: string;
  /** The name the user picked, before the collision-proof rename. */
  readonly fileName: string;
  readonly kind: MediaLibraryKind;
  readonly fileSize: number;
  readonly status: "queued" | "uploading" | "failed";
}

/**
 * `IMediaLibraryModel` — the account's library of images and videos, and the
 * resolver that turns a `campfire://media/…` reference into something a
 * `<img>` / `<video>` can load.
 *
 * **Web-first: there is no Unity counterpart.** Media on a workflow step is a
 * web-only capability, so this is modelled on `IProjectDocsModel` next door
 * rather than ported — same customer-scoped manifest shape, same
 * read-modify-write discipline, same "a failed read is never an empty
 * manifest" rule.
 *
 * ## Scoping: account-wide, with NO project join
 *
 * Unlike a document, a media file is deliberately **not** bound to projects.
 * A document is indexed into a per-project vector store, so its
 * `UsedInProjects` list is real; a media file is referenced by URL from a
 * stored `workflow.yaml` that this model never reads, and any project's
 * workflow may point at any url — including one that is not in this library at
 * all. A project list here could only ever be a guess, so the library shows the
 * whole account's media and deleting is the user's call.
 */
export interface IMediaLibraryModel {
  /** Emitted whenever the item list changes (load, upload, delete). */
  readonly onChanged: Emitter<ReadonlyArray<MediaLibraryItem>>;
  /** Emitted around manifest fetches and upload/delete batches. */
  readonly onBusyChanged: Emitter<boolean>;
  /** Free-text progress for a long upload batch (`""` when idle). */
  readonly onProgressChanged: Emitter<string>;
  /** Emitted as an upload batch is queued, advances, fails, or clears. */
  readonly onPendingChanged: Emitter<ReadonlyArray<PendingMediaUpload>>;

  /** Sorted by `LastModifiedUtc` DESC. */
  readonly items: ReadonlyArray<MediaLibraryItem>;
  /**
   * The current upload batch, in pick order. Empty when nothing is uploading —
   * except for entries that FAILED, which stay until dismissed so the tile is
   * still there to explain itself.
   */
  readonly pendingUploads: ReadonlyArray<PendingMediaUpload>;
  readonly isBusy: boolean;
  readonly progress: string;

  /**
   * Fetch + sort. Never throws — a failure yields an empty list and a console
   * warning, matching `AssetLibraryModel.loadAsync`.
   */
  loadAsync(forceReload?: boolean, signal?: AbortSignal): Promise<ReadonlyArray<MediaLibraryItem>>;

  /**
   * Uploads images / videos into the account library: S3 put → manifest upsert
   * → manifest save → reload. Each file gets a collision-proof name, so two
   * people uploading `clip.mp4` cannot overwrite one another.
   *
   * Publishes {@link pendingUploads} for the whole batch up front so the library
   * can show a placeholder tile per file rather than a spinner over an empty
   * grid.
   */
  uploadAsync(files: ReadonlyArray<File>, signal?: AbortSignal): Promise<MediaUploadResult>;

  /** Drops one FAILED pending entry (its tile's dismiss button). */
  dismissPendingUpload(id: string): void;

  /** Deletes the file and its manifest entry. Throws on failure so the caller can toast. */
  deleteAsync(storagePath: string, signal?: AbortSignal): Promise<void>;

  /**
   * Resolves a workflow `media.url` to something a browser element can load:
   * a `campfire://media/…` reference becomes a cached `blob:` URL, and any
   * other url is returned verbatim. `null` when a library reference names a
   * file that is gone.
   *
   * The MODEL owns the blob URLs it hands out — they are cached for the tab's
   * life (the same rule `thumbnailCache` and `profilePicCache` follow), so a
   * panel unmounting must never revoke one: the same media may be on screen in
   * a second surface, and the next step that uses it would get a dead URL.
   */
  resolveUrlAsync(url: string, signal?: AbortSignal): Promise<string | null>;

  /**
   * Warm the persistent byte cache for a set of `media.url`s, so a guided run
   * plays from disk instead of waiting on S3. Non-library urls are skipped
   * (the browser owns an external one), duplicates are downloaded once, and
   * anything already cached or already resolved in this tab is left alone.
   *
   * Downloads are SEQUENTIAL by construction: a workflow with several videos
   * saturating the connection in parallel would be worse than the on-demand
   * fetch this replaces. Never throws — a prefetch that fails simply leaves
   * playback to fetch on demand, exactly as before.
   *
   * Deliberately does NOT retain a blob URL per file. Holding every video a
   * workflow references alive for the tab's life is the memory cost this is
   * meant to avoid; playback reads them back off disk.
   */
  prefetchAsync(urls: ReadonlyArray<string>, signal?: AbortSignal): Promise<void>;

  /** Drop cached state (sign-out). Revokes blob URLs. */
  reset(): void;
}

/** Manifest freshness window, matching `DOCS_MANIFEST_CACHE_MS`. */
export const MEDIA_MANIFEST_CACHE_MS = 30_000;

export const MEDIA_LIBRARY_MODEL = Symbol.for("Campfire.MediaLibraryModel");
